import React, { useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Typography, Button, IconButton, Alert, CircularProgress,
  FormControl, InputLabel, Select, MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { COLORS } from "../constants";
import { useRechargeApi } from "../hooks/useRechargeApi";
import type { SubscriptionData, AddressGroup } from "../types";

interface Props {
  open: boolean;
  onClose: () => void;
  sub: SubscriptionData | null;
  addressGroups: AddressGroup[];
  onSuccess: (msg: string) => void;
  onRefresh: () => void;
}

export function MoveSubscriptionModal({ open, onClose, sub, addressGroups, onSuccess, onRefresh }: Props) {
  const { call } = useRechargeApi();
  const [targetAddressId, setTargetAddressId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    if (!sub || !targetAddressId) return;
    setLoading(true);
    setError(null);

    try {
      const res = await call("/recharge/address/move-subscription", {
        method: "POST",
        body: { subscriptionId: sub.id, targetAddressId },
      });
      if (!res.ok) throw new Error(res.error || "Failed to move subscription");
      onSuccess("Subscription moved successfully!");
      onClose();
      onRefresh();
    } catch (err: any) {
      setError(err.message || "Failed to move subscription");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography sx={{ fontWeight: 800 }}>Move Subscription</Typography>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography sx={{ mb: 2, fontSize: 14, color: COLORS.textMuted }}>
          Move <strong>{sub?.product_title}</strong> to a different address.
        </Typography>
        <FormControl fullWidth>
          <InputLabel>Target Address</InputLabel>
          <Select value={targetAddressId} onChange={(e) => setTargetAddressId(e.target.value)} label="Target Address">
            {addressGroups
              .filter(g => g.addressId !== String(sub?.address_id))
              .map(g => (
                <MenuItem key={g.addressId} value={g.addressId}>
                  {g.address ? `${g.address.address1}, ${g.address.city}` : `ID: ${g.addressId}`}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Cancel</Button>
        <Button variant="contained" onClick={handleConfirm} disabled={loading || !targetAddressId}
          sx={{ bgcolor: COLORS.brown, "&:hover": { bgcolor: COLORS.brownDark }, textTransform: "none" }}>
          {loading ? <CircularProgress size={20} color="inherit" /> : "Move"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
